import { useRef, useState, useEffect } from "react"
import { useFileDrop } from "@/hooks/useFileDrop"
import { FileText, X, Loader2, Plus, Sparkles, CheckCircle2, Circle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useToast } from "@/hooks/use-toast"
import { useThreadContext } from "@/providers/ThreadProvider"
import { uploadCaseDocument } from "@/services/storage-upload"
import { useAuth } from "@/features/auth/hooks/useAuth"
import { casesService } from "@/features/cases/services/cases.service"
import { cn } from "@/lib/utils"

/**
 * A file entry is either:
 *  - staged: the user just picked it from the OS picker (file is set, url is absent)
 *  - uploaded: previously uploaded to blob storage (url is set)
 *
 * Blob uploads are deferred until the user clicks "Generate OIO Draft".
 */
interface FileEntry {
  name: string
  file?: File    // present when staged (not yet uploaded)
  url?: string   // present when already uploaded to blob
}

export interface PreparedOioFiles {
  scn: FileEntry
  reply: FileEntry | null
  supporting: FileEntry[]
  exParte: boolean
}

export interface CaseFileUploaderProps {
  /** replyUrl is null for ex-parte cases (officer declared that no reply was filed). */
  onGenerateDraft?: (scnUrl: string, replyUrl: string | null, supportingUrls: string[], supportingFileNames?: string[]) => void
  isGenerating?: boolean
  hasDraftSessionStarted?: boolean
  /** Full-panel mode (default) vs compact strip mode */
  compact?: boolean
  /** Keep files local and hand them to the host only after Generate is confirmed. */
  stagedOnly?: boolean
  onPrepare?: (files: PreparedOioFiles) => Promise<void>
}

const ACCEPTED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]

export function CaseFileUploader({
  onGenerateDraft,
  isGenerating,
  hasDraftSessionStarted = false,
  compact = false,
  stagedOnly = false,
  onPrepare,
}: CaseFileUploaderProps) {
  const { toast } = useToast()
  const { activeThread, setActiveThread, refreshThreads } = useThreadContext()
  const { session } = useAuth()

  const [scnEntry, setScnEntry] = useState<FileEntry | null>(null)
  const [replyEntry, setReplyEntry] = useState<FileEntry | null>(null)
  // The reply is optional, but its absence must be DECLARED, not implied — a
  // forgotten upload silently producing an ex-parte order would be worse than
  // the old hard requirement (which pushed officers to mis-tag other documents
  // as the reply just to unlock the button).
  const [noReplyFiled, setNoReplyFiled] = useState(false)
  const [supportingEntries, setSupportingEntries] = useState<FileEntry[]>([])
  // True only during the blob-upload phase triggered by "Generate OIO Draft"
  const [isUploading, setIsUploading] = useState(false)

  const scnRef = useRef<HTMLInputElement>(null)
  const replyRef = useRef<HTMLInputElement>(null)
  const supportRef = useRef<HTMLInputElement>(null)

  // Restore already-uploaded file state from thread metadata whenever the active case changes.
  useEffect(() => {
    if (stagedOnly) {
      setScnEntry(null)
      setReplyEntry(null)
      setNoReplyFiled(false)
      setSupportingEntries([])
      return
    }
    if (!activeThread) {
      setScnEntry(null)
      setReplyEntry(null)
      setNoReplyFiled(false)
      setSupportingEntries([])
      return
    }
    const meta = (activeThread.metadata ?? {}) as Record<string, unknown>
    setScnEntry(
      meta.scnFileName && meta.scnFileUrl
        ? { name: meta.scnFileName as string, url: meta.scnFileUrl as string }
        : null,
    )
    const restoredReply =
      meta.replyFileName && meta.replyFileUrl
        ? { name: meta.replyFileName as string, url: meta.replyFileUrl as string }
        : null
    setReplyEntry(restoredReply)
    setNoReplyFiled(!restoredReply && meta.replyStatus === "not_received")
    const rawSupporting = meta.supportingFiles
    setSupportingEntries(
      Array.isArray(rawSupporting)
        ? (rawSupporting as Array<{ name?: string; filename?: string; url: string }>).map((f) => ({
            name: f.name ?? f.filename ?? "Supporting document",
            url: f.url,
          }))
        : [],
    )
  }, [activeThread?.id, stagedOnly])

  // Validate and stage a file locally — no network call until generate is clicked.
  const stageFile = (file: File, type: "scn" | "reply" | "supporting") => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast({ title: "Invalid file type", description: "Please upload a PDF or Word document.", variant: "destructive" })
      return
    }
    if (file.size > 20 * 1024 * 1024) {
      toast({ title: "File too large", description: "Maximum file size is 20MB.", variant: "destructive" })
      return
    }
    if (type === "scn") setScnEntry({ name: file.name, file })
    else if (type === "reply") {
      setReplyEntry({ name: file.name, file })
      setNoReplyFiled(false) // an uploaded reply overrides the no-reply declaration
    } else setSupportingEntries((prev) => [...prev, { name: file.name, file }])
  }

  const handleFileChange = (type: "scn" | "reply" | "supporting") => (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files) Array.from(files).forEach((f) => stageFile(f, type))
    e.target.value = ""
  }

  // Drag-and-drop: route each dropped file into the first empty required slot
  // (SCN, then Reply), then everything else to supporting. The officer can
  // re-tag afterwards; this just gets the files in without a click.
  const routeDroppedFiles = (files: File[]) => {
    let scnTaken = !!scnEntry
    let replyTaken = !!replyEntry || noReplyFiled
    for (const f of files) {
      if (!scnTaken) {
        stageFile(f, "scn")
        scnTaken = true
      } else if (!replyTaken) {
        stageFile(f, "reply")
        replyTaken = true
      } else {
        stageFile(f, "supporting")
      }
    }
  }
  const { isDragging, dropHandlers } = useFileDrop(routeDroppedFiles, isUploading || isGenerating)

  // Upload a single entry to blob storage and return its URL.
  // If it already has a URL (previously uploaded), returns immediately.
  const resolveEntryUrl = async (entry: FileEntry): Promise<string> => {
    if (entry.url) return entry.url
    if (!entry.file) throw new Error("No file to upload")
    return uploadCaseDocument(entry.file, session?.user.id ?? "anonymous")
  }

  /**
   * Upload any staged files, persist blob URLs to thread metadata, then invoke the parent callback.
   * This is the only place blob uploads happen — triggered exclusively by "Generate OIO Draft".
   */
  const handleGenerate = async () => {
    if (!scnEntry || !(replyEntry || noReplyFiled) || isUploading || isGenerating) return
    setIsUploading(true)
    try {
      if (stagedOnly) {
        if (!onPrepare) throw new Error("The simulator could not prepare these files.")
        await onPrepare({ scn: scnEntry, reply: replyEntry, supporting: supportingEntries, exParte: !replyEntry && noReplyFiled })
        return
      }
      const scnUrl = await resolveEntryUrl(scnEntry)
      const replyUrl = replyEntry ? await resolveEntryUrl(replyEntry) : null
      const supportingUrls: string[] = []
      for (const entry of supportingEntries) {
        supportingUrls.push(await resolveEntryUrl(entry))
      }

      // Persist the resolved URLs to thread metadata so they survive page reloads.
      // replyStatus records the officer's declaration for ex-parte cases so the
      // workspace can label the case and retry can run without a reply URL.
      if (activeThread?.id) {
        const metadata: Record<string, unknown> = {
          ...(activeThread.metadata ?? {}),
          scnFileUrl: scnUrl,
          scnFileName: scnEntry.name,
          replyFileUrl: replyUrl,
          replyFileName: replyEntry?.name ?? null,
          replyStatus: replyEntry ? "received" : "not_received",
          supportingFiles: supportingUrls.map((url, i) => ({
            name: supportingEntries[i]?.name ?? `supporting_${i + 1}`,
            url,
          })),
        }
        const updatedThread = await casesService.update(activeThread.id, { metadata })
        setActiveThread(updatedThread)
        await refreshThreads()
      }

      onGenerateDraft?.(scnUrl, replyUrl, supportingUrls, supportingEntries.map((entry) => entry.name))
    } catch (error) {
      const message = error instanceof Error ? error.message : null
      const isUnauthorized = message === "Unauthorized"
      const isUploadAuthorizationFailure = message === "Upload authorization failed."
      toast({
        title: isUnauthorized ? "Session expired" : "Upload failed",
        description: isUnauthorized
          ? "Please sign in again and retry the upload."
          : isUploadAuthorizationFailure
            ? "The upload service rejected the request. Please retry in a moment."
            : "Could not upload one or more files. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsUploading(false)
    }
  }

  const clearMainFile = async (type: "scn" | "reply") => {
    const entry = type === "scn" ? scnEntry : replyEntry
    if (type === "scn") setScnEntry(null)
    else setReplyEntry(null)

    // Only update thread metadata if the file was already uploaded (has a blob URL).
    if (entry?.url && activeThread?.id) {
      const patch = type === "scn"
        ? { scnFileUrl: null, scnFileName: null }
        : { replyFileUrl: null, replyFileName: null, replyStatus: null }
      const updatedThread = await casesService.update(activeThread.id, {
        metadata: { ...(activeThread.metadata ?? {}), ...patch },
      })
      setActiveThread(updatedThread)
    }
  }

  const clearSupportingFile = async (index: number) => {
    const entry = supportingEntries[index]
    const updated = supportingEntries.filter((_, i) => i !== index)
    setSupportingEntries(updated)

    if (entry?.url && activeThread?.id) {
      const updatedThread = await casesService.update(activeThread.id, {
        metadata: {
          ...(activeThread.metadata ?? {}),
          supportingFiles: updated.filter((e) => e.url).map((f) => ({ name: f.name, url: f.url! })),
        },
      })
      setActiveThread(updatedThread)
    }
  }

  if (!activeThread && !stagedOnly) return null

  // SCN is mandatory; the reply slot is satisfied by an upload OR the explicit
  // no-reply declaration (ex-parte).
  const readyToGenerate = Boolean(scnEntry && (replyEntry || noReplyFiled))
  const isDisabled = isUploading || Boolean(isGenerating)

  // ── Compact strip (shown inside the chat tab header) ────────────────────
  if (compact) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 flex-wrap">
        <CompactBadge label="SCN" entry={scnEntry} onUploadClick={() => scnRef.current?.click()} onClear={() => void clearMainFile("scn")} />
        <input ref={scnRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={handleFileChange("scn")} />
        {noReplyFiled && !replyEntry ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-[11px] text-amber-700">
            No reply filed (ex-parte)
            <button onClick={() => setNoReplyFiled(false)} className="hover:text-red-500 ml-0.5" aria-label="Undo no-reply declaration"><X className="w-2.5 h-2.5" /></button>
          </span>
        ) : (
          <CompactBadge label="Reply" entry={replyEntry} onUploadClick={() => replyRef.current?.click()} onClear={() => void clearMainFile("reply")} />
        )}
        <input ref={replyRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={handleFileChange("reply")} />
        {supportingEntries.map((f, i) => (
          <CompactBadge key={i} label={f.name} entry={f} onUploadClick={() => {}} onClear={() => void clearSupportingFile(i)} />
        ))}
        {isGenerating && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-2 py-0.5 text-[11px] text-primary">
            <Loader2 className="h-3 w-3 animate-spin" />
            Draft running
          </span>
        )}
      </div>
    )
  }

  // ── Full-panel mode (shown in the Documents tab) ─────────────────────────
  return (
    <div
      className={"flex flex-col gap-6 py-2 h-full overflow-y-auto relative" + (isDragging ? " na-dropzone--over" : "")}
      {...dropHandlers}
    >
      {isDragging && (
        <div className="na-dropzone__overlay">
          <FileText className="w-6 h-6" />
          <span>Drop case documents to add them</span>
        </div>
      )}
      <div>
        <h2 className="text-base font-semibold text-foreground mb-0.5">Upload Case Documents</h2>
        <p className="text-[13px] text-muted-foreground">Upload the required files, then generate the OIO draft.</p>
      </div>

      {/* Step 1 */}
      <Step
        number={1}
        label="Show Cause Notice"
        description="The GST SCN issued to the taxpayer"
        entry={scnEntry}
        onUploadClick={() => scnRef.current?.click()}
        onClear={() => void clearMainFile("scn")}
      />
      <input ref={scnRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={handleFileChange("scn")} />

      {/* Step 2 — a reply upload OR an explicit no-reply declaration */}
      <Step
        number={2}
        label="Taxpayer Reply"
        description="The reply filed by the taxpayer, if one was filed"
        entry={replyEntry}
        onUploadClick={() => replyRef.current?.click()}
        onClear={() => void clearMainFile("reply")}
        declaredEmpty={noReplyFiled}
        declaredEmptyLabel="No reply filed (ex-parte)"
        onUndoDeclareEmpty={() => setNoReplyFiled(false)}
        footer={
          !replyEntry && !noReplyFiled ? (
            <button
              onClick={() => setNoReplyFiled(true)}
              disabled={isDisabled}
              className="mt-2 text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground disabled:opacity-40 transition-colors"
              data-testid="declare-no-reply"
            >
              No reply was filed — proceed ex-parte
            </button>
          ) : null
        }
      />
      <input ref={replyRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={handleFileChange("reply")} />

      {/* Step 3 — optional */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-full border-2 border-dashed border-muted-foreground/40 flex items-center justify-center shrink-0">
            <span className="text-[11px] font-bold text-muted-foreground">3</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-medium text-foreground">Supporting Documents <span className="text-muted-foreground font-normal">(optional)</span></p>
            <p className="text-xs text-muted-foreground">Hearing notices, correspondence, etc.</p>
          </div>
        </div>
        {supportingEntries.length > 0 && (
          <div className="ml-9 space-y-1">
            {supportingEntries.map((f, i) => (
              <div key={i} className="flex items-center gap-2 rounded-md border border-dashed px-3 py-2 text-xs bg-muted/20">
                <FileText className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
                <span className="flex-1 truncate">{f.name}</span>
                <button onClick={() => void clearSupportingFile(i)} className="text-muted-foreground hover:text-destructive">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="ml-9">
          <button
            onClick={() => supportRef.current?.click()}
            disabled={isDisabled}
            className="flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground disabled:opacity-40 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add files
          </button>
        </div>
        <input ref={supportRef} type="file" accept=".pdf,.doc,.docx" multiple className="hidden" onChange={handleFileChange("supporting")} />
      </div>

      {/* Generate button */}
      <div className="pt-2 border-t mt-auto">
        {readyToGenerate ? (
          isDisabled ? (
            <div className="flex w-full items-center justify-center gap-2 rounded-md border border-primary/20 bg-primary/5 px-4 py-2 text-sm text-primary">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{isUploading ? "Uploading files..." : "Generating OIO draft..."}</span>
            </div>
          ) : hasDraftSessionStarted ? (
            <div className="rounded-md border border-muted bg-muted/30 px-4 py-3 text-center">
              <p className="text-sm font-medium text-foreground">Draft session already started</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Continue in the Chat or Analysis tab to follow OIO progress.
              </p>
            </div>
          ) : (
            <Button
              className="w-full gap-2 h-11 text-[14px]"
              onClick={() => void handleGenerate()}
              data-testid="generate-draft-button"
            >
              <Sparkles className="w-4 h-4" />
              Generate OIO Draft
            </Button>
          )
        ) : (
          <p className="text-[13px] text-center text-muted-foreground">
            Upload the SCN, then add the Taxpayer Reply — or mark that no reply was filed — to
            continue.
          </p>
        )}
      </div>
    </div>
  )
}

// ── Step component ────────────────────────────────────────────────────────────

function Step({
  number, label, description, entry, onUploadClick, onClear,
  declaredEmpty = false, declaredEmptyLabel, onUndoDeclareEmpty, footer,
}: {
  number: number
  label: string
  description: string
  entry: FileEntry | null
  onUploadClick: () => void
  onClear: () => void
  /** The slot is deliberately empty (e.g. ex-parte: no reply was ever filed). */
  declaredEmpty?: boolean
  declaredEmptyLabel?: string
  onUndoDeclareEmpty?: () => void
  footer?: React.ReactNode
}) {
  const done = Boolean(entry) || declaredEmpty
  return (
    <div className="flex items-start gap-3">
      <div className={cn(
        "w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 transition-colors",
        done ? "bg-green-100" : "border-2 border-muted-foreground/40",
      )}>
        {done
          ? <CheckCircle2 className="w-4 h-4 text-green-600" />
          : <span className="text-[11px] font-bold text-muted-foreground">{number}</span>}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold text-foreground mb-0.5">{label}</p>
        <p className="text-xs text-muted-foreground mb-2">{description}</p>

        {entry ? (
          <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2.5">
            <FileText className="w-4 h-4 text-green-600 shrink-0" />
            <span className="flex-1 truncate text-[13px] text-foreground">{entry.name}</span>
            <button onClick={onClear} className="text-muted-foreground hover:text-destructive transition-colors" aria-label="Remove">
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : declaredEmpty ? (
          <div
            className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5"
            data-testid="no-reply-declared"
          >
            <FileText className="w-4 h-4 text-amber-600 shrink-0" />
            <span className="flex-1 truncate text-[13px] text-foreground">{declaredEmptyLabel}</span>
            <button
              onClick={onUndoDeclareEmpty}
              className="text-muted-foreground hover:text-destructive transition-colors"
              aria-label="Undo no-reply declaration"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button
            onClick={onUploadClick}
            className={cn(
              "w-full flex items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-3.5",
              "text-[13px] text-muted-foreground hover:border-primary hover:text-primary",
              "transition-colors",
            )}
          >
            <Circle className="w-4 h-4" /> Click to upload — PDF or DOCX, max 20 MB
          </button>
        )}
        {footer}
      </div>
    </div>
  )
}

// ── Compact badge (used in the chat tab header) ───────────────────────────────

function CompactBadge({ label, entry, onUploadClick, onClear }: {
  label: string; entry: FileEntry | null; onUploadClick: () => void; onClear: () => void
}) {
  if (entry) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 border border-green-200 text-[11px] text-green-700">
        <CheckCircle2 className="w-3 h-3" />{entry.name.length > 20 ? entry.name.slice(0, 18) + "…" : entry.name}
        <button onClick={onClear} className="hover:text-red-500 ml-0.5"><X className="w-2.5 h-2.5" /></button>
      </span>
    )
  }
  return (
    <button onClick={onUploadClick} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-dashed text-[11px] text-muted-foreground hover:text-foreground">
      <Plus className="w-3 h-3" />{label}
    </button>
  )
}
